import React, { useState } from 'react';
import { 
  DollarSign, 
  Calculator, 
  Building, 
  Search, 
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  FileSpreadsheet,
  Package,
  Timer,
  MapPin,
  Truck,
  Settings,
  CheckCircle,
  AlertCircle,
  Copy,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';

const PriceConfiguration = () => {
  const { toast } = useToast();

  // Mock data for price configurations
  const [priceConfigs] = useState([
    {
      id: 'PC001',
      name: 'Bảng giá Hà Nội - Miền Bắc',
      code: 'HN_NORTH',
      hubId: 'HUB001',
      hubName: 'Trung tâm khai thác Hà Nội',
      serviceType: 'Hỏa tốc',
      effectiveDate: '2024-01-01',
      expiryDate: '2024-12-31',
      basePriceInner: 25000,
      basePriceOuter: 35000,
      basePriceRemote: 45000,
      weightStep: 1.0,
      weightPriceInner: 8000,
      weightPriceOuter: 12000,
      weightPriceRemote: 15000,
      distanceStep: 50,
      distancePriceInner: 0,
      distancePriceOuter: 500,
      distancePriceRemote: 1000,
      maxWeight: 30,
      maxDistance: 100,
      codFeePercent: 1.5,
      codMinFee: 5000,
      codMaxFee: 50000,
      packagingFee: 5000,
      insuranceFeePercent: 0.5,
      insuranceMinFee: 10000,
      fragileHandlingFee: 15000,
      returnFee: 20000,
      changeFee: 10000,
      status: 'active',
      priority: 1,
      description: 'Bảng giá chuẩn cho khu vực Hà Nội và miền Bắc',
      createdAt: '2024-01-01T08:00:00+07:00',
      updatedAt: '2024-01-15T08:00:00+07:00',
      orderCount: 1250,
      totalRevenue: 48750000
    },
    {
      id: 'PC002',
      name: 'Bảng giá TP.HCM - Miền Nam',
      code: 'HCM_SOUTH',
      hubId: 'HUB007',
      hubName: 'Trung tâm khai thác TP.HCM',
      serviceType: 'Nhanh',
      effectiveDate: '2024-01-01',
      expiryDate: '2024-12-31',
      basePriceInner: 30000,
      basePriceOuter: 40000,
      basePriceRemote: 50000,
      weightStep: 1.0,
      weightPriceInner: 10000,
      weightPriceOuter: 14000,
      weightPriceRemote: 18000,
      distanceStep: 50,
      distancePriceInner: 0,
      distancePriceOuter: 800,
      distancePriceRemote: 1200,
      maxWeight: 50,
      maxDistance: 200,
      codFeePercent: 2.0,
      codMinFee: 8000,
      codMaxFee: 80000,
      packagingFee: 8000,
      insuranceFeePercent: 0.8,
      insuranceMinFee: 15000,
      fragileHandlingFee: 20000,
      returnFee: 25000,
      changeFee: 12000,
      status: 'active',
      priority: 2,
      description: 'Bảng giá cho khu vực TP.HCM và miền Nam',
      createdAt: '2024-01-01T08:00:00+07:00',
      updatedAt: '2024-01-10T08:00:00+07:00',
      orderCount: 980,
      totalRevenue: 39200000
    },
    {
      id: 'PC003',
      name: 'Bảng giá Đà Nẵng - Miền Trung',
      code: 'DN_CENTRAL',
      hubId: 'HUB003',
      hubName: 'Trung tâm khai thác Đà Nẵng',
      serviceType: 'Tiết kiệm',
      effectiveDate: '2024-01-01',
      expiryDate: '2024-12-31',
      basePriceInner: 20000,
      basePriceOuter: 28000,
      basePriceRemote: 38000,
      weightStep: 1.0,
      weightPriceInner: 6000,
      weightPriceOuter: 9000,
      weightPriceRemote: 12000,
      distanceStep: 50,
      distancePriceInner: 0,
      distancePriceOuter: 400,
      distancePriceRemote: 800,
      maxWeight: 25,
      maxDistance: 80,
      codFeePercent: 1.2,
      codMinFee: 4000,
      codMaxFee: 40000,
      packagingFee: 4000,
      insuranceFeePercent: 0.4,
      insuranceMinFee: 8000,
      fragileHandlingFee: 12000,
      returnFee: 15000,
      changeFee: 8000,
      status: 'inactive',
      priority: 3,
      description: 'Bảng giá cho khu vực Đà Nẵng và miền Trung',
      createdAt: '2024-01-01T08:00:00+07:00',
      updatedAt: '2024-01-05T08:00:00+07:00',
      orderCount: 456,
      totalRevenue: 18240000
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedConfigs, setSelectedConfigs] = useState([]);
  const [showCalculator, setShowCalculator] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    hubId: '',
    serviceType: '',
    effectiveDate: '',
    expiryDate: '',
    basePriceInner: '',
    basePriceOuter: '',
    basePriceRemote: '',
    weightStep: '1.0',
    weightPriceInner: '',
    weightPriceOuter: '',
    weightPriceRemote: '',
    distanceStep: '50',
    distancePriceInner: '0',
    distancePriceOuter: '',
    distancePriceRemote: '',
    maxWeight: '',
    maxDistance: '',
    codFeePercent: '',
    codMinFee: '',
    codMaxFee: '',
    packagingFee: '',
    insuranceFeePercent: '',
    insuranceMinFee: '',
    fragileHandlingFee: '',
    returnFee: '',
    changeFee: '',
    status: 'active',
    priority: '1',
    description: ''
  });

  // Calculator state
  const [calculatorData, setCalculatorData] = useState({
    configId: '',
    weight: '',
    distance: '',
    areaType: 'inner',
    declaredValue: '',
    isCOD: false,
    isFragile: false,
    needInsurance: false,
    needPackaging: false
  });

  const filteredConfigs = priceConfigs.filter(config => {
    const matchesSearch = config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         config.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         config.hubName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || config.status === statusFilter;
    const matchesService = serviceFilter === 'all' || config.serviceType === serviceFilter;
    return matchesSearch && matchesStatus && matchesService;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'expired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang áp dụng';
      case 'inactive':
        return 'Tạm dừng';
      case 'pending':
        return 'Chờ áp dụng';
      case 'expired':
        return 'Hết hạn';
      default:
        return 'Không xác định';
    }
  };

  const getServiceTypeColor = (type: string) => {
    switch (type) {
      case 'Hỏa tốc':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Nhanh':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Tiết kiệm':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const calculateEstimatedPrice = (config: any, weight = 1, distance = 10, areaType = 'inner') => {
    let basePrice = 0;
    let weightPrice = 0;
    let distancePrice = 0;

    // Giá cơ bản theo khu vực
    switch (areaType) {
      case 'inner':
        basePrice = config.basePriceInner;
        weightPrice = config.weightPriceInner;
        distancePrice = config.distancePriceInner;
        break;
      case 'outer':
        basePrice = config.basePriceOuter;
        weightPrice = config.weightPriceOuter;
        distancePrice = config.distancePriceOuter;
        break;
      case 'remote':
        basePrice = config.basePriceRemote;
        weightPrice = config.weightPriceRemote;
        distancePrice = config.distancePriceRemote;
        break;
    }

    // Tính phí theo cân nặng
    const weightSteps = Math.ceil(weight / config.weightStep);
    const totalWeightPrice = weightSteps * weightPrice;

    // Tính phí theo khoảng cách
    const distanceSteps = Math.ceil(distance / config.distanceStep);
    const totalDistancePrice = distanceSteps * distancePrice;

    return basePrice + totalWeightPrice + totalDistancePrice;
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      code: '',
      hubId: '',
      serviceType: '',
      effectiveDate: '',
      expiryDate: '',
      basePriceInner: '',
      basePriceOuter: '',
      basePriceRemote: '',
      weightStep: '1.0',
      weightPriceInner: '',
      weightPriceOuter: '',
      weightPriceRemote: '',
      distanceStep: '50',
      distancePriceInner: '0',
      distancePriceOuter: '',
      distancePriceRemote: '',
      maxWeight: '',
      maxDistance: '',
      codFeePercent: '',
      codMinFee: '',
      codMaxFee: '',
      packagingFee: '',
      insuranceFeePercent: '',
      insuranceMinFee: '',
      fragileHandlingFee: '',
      returnFee: '',
      changeFee: '',
      status: 'active',
      priority: '1',
      description: ''
    });
    setShowDialog(true);
  };

  const handleEdit = (config: any) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      code: config.code,
      hubId: config.hubId,
      serviceType: config.serviceType,
      effectiveDate: config.effectiveDate,
      expiryDate: config.expiryDate,
      basePriceInner: config.basePriceInner.toString(),
      basePriceOuter: config.basePriceOuter.toString(),
      basePriceRemote: config.basePriceRemote.toString(),
      weightStep: config.weightStep.toString(),
      weightPriceInner: config.weightPriceInner.toString(),
      weightPriceOuter: config.weightPriceOuter.toString(),
      weightPriceRemote: config.weightPriceRemote.toString(),
      distanceStep: config.distanceStep.toString(),
      distancePriceInner: config.distancePriceInner.toString(),
      distancePriceOuter: config.distancePriceOuter.toString(),
      distancePriceRemote: config.distancePriceRemote.toString(),
      maxWeight: config.maxWeight.toString(),
      maxDistance: config.maxDistance.toString(),
      codFeePercent: config.codFeePercent.toString(),
      codMinFee: config.codMinFee.toString(),
      codMaxFee: config.codMaxFee.toString(),
      packagingFee: config.packagingFee.toString(),
      insuranceFeePercent: config.insuranceFeePercent.toString(),
      insuranceMinFee: config.insuranceMinFee.toString(),
      fragileHandlingFee: config.fragileHandlingFee.toString(),
      returnFee: config.returnFee.toString(),
      changeFee: config.changeFee.toString(),
      status: config.status,
      priority: config.priority.toString(),
      description: config.description
    });
    setShowDialog(true);
  };

  const handleCopy = (config: any) => {
    setEditingConfig(null);
    setFormData({
      name: `Copy of ${config.name}`,
      code: `${config.code}_COPY`,
      hubId: config.hubId,
      serviceType: config.serviceType,
      effectiveDate: '',
      expiryDate: '',
      basePriceInner: config.basePriceInner.toString(),
      basePriceOuter: config.basePriceOuter.toString(),
      basePriceRemote: config.basePriceRemote.toString(),
      weightStep: config.weightStep.toString(),
      weightPriceInner: config.weightPriceInner.toString(),
      weightPriceOuter: config.weightPriceOuter.toString(),
      weightPriceRemote: config.weightPriceRemote.toString(),
      distanceStep: config.distanceStep.toString(),
      distancePriceInner: config.distancePriceInner.toString(),
      distancePriceOuter: config.distancePriceOuter.toString(),
      distancePriceRemote: config.distancePriceRemote.toString(),
      maxWeight: config.maxWeight.toString(),
      maxDistance: config.maxDistance.toString(),
      codFeePercent: config.codFeePercent.toString(),
      codMinFee: config.codMinFee.toString(),
      codMaxFee: config.codMaxFee.toString(),
      packagingFee: config.packagingFee.toString(),
      insuranceFeePercent: config.insuranceFeePercent.toString(),
      insuranceMinFee: config.insuranceMinFee.toString(),
      fragileHandlingFee: config.fragileHandlingFee.toString(),
      returnFee: config.returnFee.toString(),
      changeFee: config.changeFee.toString(),
      status: 'inactive',
      priority: '1',
      description: config.description
    });
    setShowDialog(true);
  };

  const handleDelete = (configId: string) => {
    toast({
      title: "Xóa cấu hình giá",
      description: "Cấu hình giá đã được xóa thành công",
    });
  };

  const handleSave = () => {
    toast({
      title: editingConfig ? "Cập nhật thành công" : "Tạo mới thành công", 
      description: editingConfig ? "Cấu hình giá đã được cập nhật" : "Cấu hình giá mới đã được tạo",
    });
    setShowDialog(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cấu hình giá</h1>
          <p className="text-muted-foreground">Quản lý bảng giá dịch vụ vận chuyển</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowCalculator(true)}
          >
            <Calculator className="h-4 w-4 mr-2" />
            Máy tính giá
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Tạo bảng giá mới
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng bảng giá</p>
                <p className="text-2xl font-bold">{priceConfigs.length}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Đang áp dụng</p>
                <p className="text-2xl font-bold text-green-600">
                  {priceConfigs.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tạm dừng</p>
                <p className="text-2xl font-bold text-red-600">
                  {priceConfigs.filter(c => c.status === 'inactive').length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng doanh thu</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(priceConfigs.reduce((sum, c) => sum + c.totalRevenue, 0))}
                </p>
              </div>
              <Building className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm theo tên, mã hoặc bưu cục..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Lọc theo trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả trạng thái</SelectItem>
                <SelectItem value="active">Đang áp dụng</SelectItem>
                <SelectItem value="inactive">Tạm dừng</SelectItem>
                <SelectItem value="pending">Chờ áp dụng</SelectItem>
                <SelectItem value="expired">Hết hạn</SelectItem>
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Truck className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Lọc theo dịch vụ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả dịch vụ</SelectItem>
                <SelectItem value="Hỏa tốc">Hỏa tốc</SelectItem>
                <SelectItem value="Nhanh">Nhanh</SelectItem>
                <SelectItem value="Tiết kiệm">Tiết kiệm</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Price Configurations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách bảng giá</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tên bảng giá</TableHead>
                  <TableHead>Mã</TableHead>
                  <TableHead>Bưu cục</TableHead>
                  <TableHead>Dịch vụ</TableHead>
                  <TableHead>Giá cơ bản</TableHead>
                  <TableHead>Hiệu lực</TableHead>
                  <TableHead>Đơn hàng</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead>Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConfigs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{config.name}</div>
                        <div className="text-sm text-muted-foreground">{config.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{config.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        <span className="text-sm">{config.hubName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getServiceTypeColor(config.serviceType)}>
                        {config.serviceType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>NT: {formatCurrency(config.basePriceInner)}</div>
                        <div>NGT: {formatCurrency(config.basePriceOuter)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{formatDate(config.effectiveDate)}</div>
                        <div className="text-muted-foreground">đến {formatDate(config.expiryDate)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{config.orderCount} đơn</div>
                        <div className="text-muted-foreground">{formatCurrency(config.totalRevenue)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(config.status)}>
                        {getStatusText(config.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(config)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(config)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCopy(config)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(config.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Chỉnh sửa bảng giá' : 'Tạo bảng giá mới'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Thông tin cơ bản</TabsTrigger>
              <TabsTrigger value="pricing">Bảng giá</TabsTrigger>
              <TabsTrigger value="fees">Phí dịch vụ</TabsTrigger>
              <TabsTrigger value="settings">Cài đặt</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Tên bảng giá *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nhập tên bảng giá"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Mã bảng giá *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Nhập mã bảng giá"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hubId">Bưu cục áp dụng *</Label>
                  <Select value={formData.hubId} onValueChange={(value) => setFormData({...formData, hubId: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn bưu cục" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HUB001">Trung tâm khai thác Hà Nội</SelectItem>
                      <SelectItem value="HUB007">Trung tâm khai thác TP.HCM</SelectItem>
                      <SelectItem value="HUB003">Trung tâm khai thác Đà Nẵng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serviceType">Loại dịch vụ *</Label>
                  <Select value={formData.serviceType} onValueChange={(value) => setFormData({...formData, serviceType: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn dịch vụ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hỏa tốc">Hỏa tốc</SelectItem>
                      <SelectItem value="Nhanh">Nhanh</SelectItem>
                      <SelectItem value="Tiết kiệm">Tiết kiệm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effectiveDate">Ngày hiệu lực *</Label>
                  <Input
                    id="effectiveDate"
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Ngày hết hạn *</Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Mô tả</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Nhập mô tả cho bảng giá"
                />
              </div>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center font-medium">Nội thành</div>
                <div className="text-center font-medium">Ngoại thành</div>
                <div className="text-center font-medium">Vùng xa</div>
                
                <div className="space-y-2">
                  <Label>Giá cơ bản (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.basePriceInner}
                    onChange={(e) => setFormData({...formData, basePriceInner: e.target.value})}
                    placeholder="25000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Giá cơ bản (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.basePriceOuter}
                    onChange={(e) => setFormData({...formData, basePriceOuter: e.target.value})}
                    placeholder="35000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Giá cơ bản (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.basePriceRemote}
                    onChange={(e) => setFormData({...formData, basePriceRemote: e.target.value})}
                    placeholder="45000"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Giá theo kg (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.weightPriceInner}
                    onChange={(e) => setFormData({...formData, weightPriceInner: e.target.value})}
                    placeholder="8000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Giá theo kg (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.weightPriceOuter}
                    onChange={(e) => setFormData({...formData, weightPriceOuter: e.target.value})}
                    placeholder="12000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Giá theo kg (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.weightPriceRemote}
                    onChange={(e) => setFormData({...formData, weightPriceRemote: e.target.value})}
                    placeholder="15000"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bước cân nặng (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weightStep}
                    onChange={(e) => setFormData({...formData, weightStep: e.target.value})}
                    placeholder="1.0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cân nặng tối đa (kg)</Label>
                  <Input
                    type="number"
                    value={formData.maxWeight}
                    onChange={(e) => setFormData({...formData, maxWeight: e.target.value})}
                    placeholder="30"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="fees" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phí COD (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.codFeePercent}
                    onChange={(e) => setFormData({...formData, codFeePercent: e.target.value})}
                    placeholder="1.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí COD tối thiểu (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.codMinFee}
                    onChange={(e) => setFormData({...formData, codMinFee: e.target.value})}
                    placeholder="5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí bảo hiểm (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.insuranceFeePercent}
                    onChange={(e) => setFormData({...formData, insuranceFeePercent: e.target.value})}
                    placeholder="0.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí bảo hiểm tối thiểu (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.insuranceMinFee}
                    onChange={(e) => setFormData({...formData, insuranceMinFee: e.target.value})}
                    placeholder="10000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí đóng gói (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.packagingFee}
                    onChange={(e) => setFormData({...formData, packagingFee: e.target.value})}
                    placeholder="5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí xử lý hàng dễ vỡ (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.fragileHandlingFee}
                    onChange={(e) => setFormData({...formData, fragileHandlingFee: e.target.value})}
                    placeholder="15000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí hoàn hàng (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.returnFee}
                    onChange={(e) => setFormData({...formData, returnFee: e.target.value})}
                    placeholder="20000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phí thay đổi (VNĐ)</Label>
                  <Input
                    type="number"
                    value={formData.changeFee}
                    onChange={(e) => setFormData({...formData, changeFee: e.target.value})}
                    placeholder="10000"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Trạng thái</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Đang áp dụng</SelectItem>
                      <SelectItem value="inactive">Tạm dừng</SelectItem>
                      <SelectItem value="pending">Chờ áp dụng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Độ ưu tiên</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Cao nhất</SelectItem>
                      <SelectItem value="2">Cao</SelectItem>
                      <SelectItem value="3">Trung bình</SelectItem>
                      <SelectItem value="4">Thấp</SelectItem>
                      <SelectItem value="5">Thấp nhất</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Hủy
            </Button>
            <Button onClick={handleSave}>
              {editingConfig ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceConfiguration; 